import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import moment from "moment";
import { CircleAlert } from "lucide-react";
import DeleteModal from "@/components/Common/DeleteModal";
import { pageURLs } from "@/constants/pageURLs";
import { compactPrice } from "@/helpers/pastClientHomes";
import {
  IMPORT_REVIEW_QUERY_KEY,
  useDiscardImportedDraft,
  useImportReview,
  usePublishImportedDraft,
} from "@/hooks/useImportReview";
import "./ImportReviewInbox.scss";

const VISIBLE_ROWS = 3;

const SOURCE_NAMES = { csv: "your CSV", follow_up_boss: "Follow Up Boss" };

const criteriaLine = (draft) =>
  [
    draft.beds ? `${draft.beds}+ beds` : null,
    draft.baths ? `${draft.baths}+ baths` : null,
    draft.maxPrice
      ? `${draft.minPrice ? `${compactPrice(draft.minPrice)} to ` : "Up to "}${compactPrice(draft.maxPrice)}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

const sourceLine = (draft) => {
  const source = SOURCE_NAMES[draft.source];
  if (!source) return null;
  if (draft.refreshedAt) {
    return `Updated from ${source} ${moment.utc(draft.refreshedAt).local().format("MMM D")}`;
  }
  if (draft.importedAt) {
    return `Imported from ${source} ${moment.utc(draft.importedAt).local().format("MMM D")}`;
  }
  return `Imported from ${source}`;
};

const needsLocation = (draft) => draft.hasLocation === false;

const ImportReviewInbox = ({ onChanged }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [publishing, setPublishing] = useState(null);
  const [discarding, setDiscarding] = useState(null);
  const [agreement, setAgreement] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const query = useImportReview();
  const drafts = query.data ?? [];

  const settle = () => {
    queryClient.invalidateQueries({ queryKey: IMPORT_REVIEW_QUERY_KEY });
    if (onChanged) onChanged();
  };

  const publish = usePublishImportedDraft({
    onSuccess: (result) => {
      if (result?.status !== "published") {
        if (result?.status !== "draft") {
          setPublishing(null);
          setAgreement(false);
        }
        toast.error("Couldn't publish it. Please try again.");
        return;
      }
      setPublishing(null);
      setAgreement(false);
      toast.success("Published to your MLS/association network.");
    },
    onError: (err) =>
      toast.error(err?.message || "Couldn't publish it. Please try again."),
    onSettled: settle,
  });

  const discard = useDiscardImportedDraft({
    onSuccess: () => {
      setDiscarding(null);
      toast.success("Draft discarded.", { autoClose: 1500 });
    },
    onError: () => toast.error("Couldn't discard it. Please try again."),
    onSettled: settle,
  });

  const edit = (draft) =>
    navigate(`${pageURLs.agentCreatewantad}?wantAdId=${draft.wantAdId}`);

  if (query.isError) {
    return (
      <p className="import-review-inbox-error" role="alert">
        <CircleAlert size={16} aria-hidden />
        Couldn't load your drafts to review.{" "}
        <button type="button" onClick={() => query.refetch()}>
          Try again
        </button>
      </p>
    );
  }

  if (!drafts.length) return null;

  const visible = showAll ? drafts : drafts.slice(0, VISIBLE_ROWS);

  return (
    <section className="import-review-inbox" aria-labelledby="iri-title">
      <div className="iri-head">
        <h3 id="iri-title">
          Ready to review ({drafts.length.toLocaleString("en-US")})
        </h3>
      </div>
      <ul className="iri-list">
        {visible.map((draft) => (
          <li className="iri-row" key={draft.wantAdId}>
            <div className="iri-row__main">
              <span className="iri-row__title">{draft.title}</span>
              <span className="iri-row__meta">
                {[draft.clientName, criteriaLine(draft)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
              {needsLocation(draft) ? (
                <span className="iri-row__note">
                  <CircleAlert size={14} aria-hidden />
                  {draft.note || "Add a location before publishing"}
                </span>
              ) : (
                sourceLine(draft) && (
                  <span className="iri-row__source">{sourceLine(draft)}</span>
                )
              )}
            </div>
            <div className="iri-row__actions">
              {needsLocation(draft) ? (
                <button
                  type="button"
                  className="btn btn-primary iri-btn"
                  onClick={() => edit(draft)}
                >
                  Add a location
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-primary iri-btn"
                    onClick={() => {
                      setAgreement(false);
                      setPublishing(draft);
                    }}
                  >
                    Publish
                  </button>
                  <button
                    type="button"
                    className="btn btn-tertiary iri-btn"
                    onClick={() => edit(draft)}
                  >
                    Edit
                  </button>
                </>
              )}
              <button
                type="button"
                className="iri-link"
                disabled={discard.isPending}
                onClick={() => setDiscarding(draft)}
              >
                Discard
              </button>
            </div>
          </li>
        ))}
      </ul>
      {drafts.length > VISIBLE_ROWS && (
        <button
          type="button"
          className="iri-more"
          aria-expanded={showAll}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? "Show fewer"
            : `Show all ${drafts.length.toLocaleString("en-US")} drafts`}
        </button>
      )}

      <Modal
        show={!!publishing}
        onHide={() => setPublishing(null)}
        centered
        aria-labelledby="iri-publish-title"
      >
        <Modal.Body className="import-review-publish">
          <h2 id="iri-publish-title">Publish this Buyer Want-Listing?</h2>
          <div className="iri-summary">
            <span className="iri-summary__title">{publishing?.title}</span>
            {publishing && (
              <span className="iri-summary__meta">
                {[publishing.clientName, criteriaLine(publishing)]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </div>
          <p>
            Members of your MLS/association network see it and can send
            introductions for homes that&nbsp;fit.
          </p>
          <label className="iri-agreement">
            <input
              type="checkbox"
              checked={agreement}
              onChange={(e) => setAgreement(e.target.checked)}
            />
            <span>I have a signed buyer agreement with this client.</span>
          </label>
          <div className="iri-modal-actions">
            <button
              type="button"
              className="btn btn-tertiary"
              onClick={() => setPublishing(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!agreement || publish.isPending}
              onClick={() => publish.mutate({ wantAdId: publishing.wantAdId, agreement })}
            >
              Publish
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <DeleteModal
        show={!!discarding}
        onHide={() => setDiscarding(null)}
        title="Discard this draft?"
        message={`${discarding?.title || "This draft"} is removed from Ready to review. The client stays in My Clients.`}
        buttonText="Discard"
        handleConfirm={() => {
          if (!discard.isPending) discard.mutate(discarding.wantAdId);
        }}
      />
    </section>
  );
};

export default ImportReviewInbox;
